import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import GuideCard from 'components/GuideCard';
import useMatchMedia from 'hooks/useMatchMedia';

import './GroupDiscoveryHint.styles.scss';

const HINT_SELECTOR = '.group-discovery-hint';
const CARD_SELECTOR = '.guide-card';

// Keep the card open for at least this long once shown, so a quick pointer pass
// can't flash it open/closed (or prematurely mark the feature discovered).
// Measured once from the moment the card opens: a pointer that lingers longer
// than this closes the card on leave, exactly like a plain hover.
const MIN_OPEN_MS = 3000;

// How often to re-check once the minimum has elapsed but the pointer is still
// on the trigger or the card.
const HOVER_RECHECK_MS = 150;

type GroupDiscoveryHintProps = {
  className?: string;
  // i18n keys — GuideCard translates them internally.
  title: string;
  description: string;
  // The pulsing trigger the card is anchored to (dot for a member, group pill).
  anchor: React.ReactNode;
  // Card closed (pointer leave on desktop / outside tap on touch) -> discover.
  onClose: () => void;
};

const GroupDiscoveryHint: React.FC<GroupDiscoveryHintProps> = (props) => {
  const { className, title, description, anchor, onClose } = props;

  // Open state is local: opening (hover / tap) does not update preferences, so
  // it never remounts this cell. Discovery happens only on close.
  const [open, setOpen] = useState(false);

  const openedAtRef = useRef(0);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const hintRef = useRef<HTMLSpanElement>(null);

  const isTouch = useMatchMedia('isTouchDevice');

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  }, []);

  const doClose = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
    onClose();
  }, [clearCloseTimer, onClose]);

  const openCard = useCallback(() => {
    clearCloseTimer();

    // Re-entering the trigger while the card is still up cancels the pending
    // close but keeps the original deadline, so it never gets pushed further
    // out by repeated hovering.
    if (!open) openedAtRef.current = Date.now();

    setOpen(true);
  }, [clearCloseTimer, open]);

  // The card is portaled beside the trigger rather than nested inside it, so
  // moving the pointer onto the card fires a leave on the trigger. `:hover` is
  // the only thing that still tells the truth about where the pointer is.
  const isPointerOver = useCallback(() => {
    if (hintRef.current?.matches(':hover')) return true;

    return Array.from(document.querySelectorAll(CARD_SELECTOR)).some((card) =>
      card.matches(':hover'),
    );
  }, []);

  // Desktop: close once the minimum open time has elapsed *and* the pointer has
  // actually left both the trigger and the card. Re-entering the trigger
  // cancels a pending close.
  const requestClose = useCallback(() => {
    const remaining = Math.max(
      MIN_OPEN_MS - (Date.now() - openedAtRef.current),
      0,
    );

    clearCloseTimer();

    closeTimerRef.current = setTimeout(function closeWhenPointerAway() {
      if (isPointerOver()) {
        closeTimerRef.current = setTimeout(
          closeWhenPointerAway,
          HOVER_RECHECK_MS,
        );
        return;
      }

      doClose();
    }, remaining);
  }, [clearCloseTimer, doClose, isPointerOver]);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  // On touch there is no pointer-leave, so an outside tap closes the card.
  useEffect(() => {
    if (!isTouch || !open) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (target.closest(HINT_SELECTOR) || target.closest(CARD_SELECTOR))
        return;

      doClose();
    };

    window.addEventListener('click', handleClick);

    return () => window.removeEventListener('click', handleClick);
  }, [isTouch, open, doClose]);

  // The mouse handlers live on a wrapper inside FloatingBanner's anchor slot (a
  // sibling of the portaled card, not its ancestor) so onMouseLeave fires when
  // the pointer leaves the trigger. Touch: tap opens the card.
  const wrappedAnchor = (
    <span
      className="group-discovery-hint__anchor"
      onClick={(e) => {
        // Touch has no hover, so the tap itself is what opens the card — it
        // must not also reach the row / pill underneath.
        if (isTouch) {
          e.stopPropagation();
          openCard();
          return;
        }

        // Desktop: the click belongs to whatever the hint is anchored to (the
        // member row, the group pill). Dismiss the card and let it through.
        doClose();
      }}
      {...(!isTouch && { onMouseEnter: openCard, onMouseLeave: requestClose })}
    >
      {anchor}
    </span>
  );

  return (
    <span
      ref={hintRef}
      className={classNames('group-discovery-hint', className)}
    >
      <GuideCard
        showOnPhablet
        hasCloseButton={false}
        showBannerNotification={open}
        anchor={wrappedAnchor}
        title={title}
        description={description}
      />
    </span>
  );
};

export default GroupDiscoveryHint;
